"use client";

import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useTransition } from "react";
import { routing } from "@/i18n/routing";

type TargetLocale = (typeof routing.locales)[number];

function altPathFromHead(locale: TargetLocale): string | null {
    if (typeof document === "undefined") return null;
    const link = document.head.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${locale}"]`,
    );
    if (!link?.href) return null;
    try {
        const url = new URL(link.href, window.location.origin);
        let path = url.pathname || "/";
        const prefix = `/${locale}`;
        if (path === prefix) return "/";
        if (path.startsWith(`${prefix}/`)) path = path.slice(prefix.length);
        return path || "/";
    } catch {
        return null;
    }
}

export default function LangSwitcher() {
    const locale = useLocale();
    const t = useTranslations("lang");
    const router = useRouter();
    const pathname = usePathname();
    const [pending, start] = useTransition();

    const next = (locale === "en" ? "es" : "en") as TargetLocale;
    const label = next === "en" ? t("en") : t("es");

    const onClick = () => {
        start(() => {
            const alt = altPathFromHead(next);
            if (alt) {
                router.replace(alt, { locale: next });
                return;
            }
            if (/^\/blog\/.+/.test(pathname)) {
                router.replace("/blog", { locale: next });
                return;
            }
            router.replace(pathname, { locale: next });
        });
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={pending}
            aria-label={`${label} - ${t("switchAria")}`}
            title={label}
            className="inline-flex h-9 items-center justify-center rounded-md px-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
            {label}
        </button>
    );
}
